(ns frontend.view.modals.curtains.put-on-hold-curtain.view
  (:require
   [clojure.string :as string]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.modals.curtains.put-on-hold-curtain.events :as events]
   [potpuri.core :refer [fn->]]
   [reagent.core :as r]
   [ui.color :as c]
   [ui.components.input.checkbox :as checkbox]
   [ui.components.modal.view :as modal]
   [ui.components.toggle.view :as toggle]
   [ui.editors.data-input :as data-input]
   [ui.editors.labelled-input.view :as labelled-input]))

(defonce modal-key :curtain/put-on-hold)

(defonce preset-months [1 2 3 6 12])

(defn month-selector
  [edit-state]
  (let [attribute :months
        value (get @edit-state attribute)
        options (concat (->> preset-months
                             (map (fn [num]
                                    {:value num
                                     :label (if (= num 1)
                                              (loc/tr :month-one)
                                              (loc/tr :months-num [num]))})))
                        [{:value 0
                          :label (loc/tr :select-custom-date)}])]
    [:div
     [labelled-input/view
      {:editor-type :editor.type/select
       :label       (loc/tr :until)
       :mandatory?  true
       :options     options
       :value       (->> options
                         (cu/filter-by :value value)
                         (first))
       :on-change   (fn [selected-value _selected-option]
                      (if (nil? selected-value)
                        (swap! edit-state dissoc attribute selected-value)
                        (swap! edit-state assoc attribute selected-value)))}]]))

(defn until-selector
  [edit-state]
  [:div
   [data-input/view
    {:data-type   :data.type/datetime
     :placeholder (loc/tr :select-date)
     :full-width? true
     :value       (:until @edit-state)
     :on-change   (fn [utc-ms]
                    (swap! edit-state assoc :until utc-ms))}]])

(defn internal-comment-input
  [edit-state]
  (let [attribute :internal-comment
        value (get @edit-state attribute)]
    [:div
     [labelled-input/view
      {:label     (loc/tr :put-on-hold-curtain/internal-comment)
       :data-type :data.type/text-multiline
       :value     value
       :on-change (fn [new-value]
                    (if (nil? new-value)
                      (swap! edit-state dissoc attribute new-value)
                      (swap! edit-state assoc attribute new-value)))}]]))

(defn notification-toggle
  [edit-state tooltip]
  (let [read-only? (boolean tooltip)
        attribute :send-notification
        value (if read-only?
                false
                (get @edit-state attribute))]
    [:div
     [:div
      {:class "flex items-center gap-2"}
      [:label (loc/tr :put-on-hold-curtain/notify-contact-person)]
      [toggle/toggle
       {:checked?  (if read-only?
                     false
                     value)
        :disabled? read-only?
        :on-change (fn [checked?]
                     (swap! edit-state assoc attribute checked?))}]]]))

(defn to-selector
  [edit-state persons]
  (let [attribute :notify-to
        read-only? (= (count persons) 1)
        value (get @edit-state attribute)
        options (->> persons
                     (map (fn [c]
                            {:value (:db/id c)
                             :label (entity/display-string c :long)})))]
    [:div
     [labelled-input/view
      {:editor-type :editor.type/select
       :label       (str (loc/tr :email/to) ":")
       :disabled?   read-only?
       :options     options
       :value       (->> options
                         (cu/filter-by :value value)
                         (first))
       :on-change   (fn [selected-value _selected-option]
                      (if (nil? selected-value)
                        (swap! edit-state dissoc attribute selected-value)
                        (swap! edit-state assoc attribute selected-value)))}]]))

(defn subject-input
  [edit-state]
  (let [attribute :subject
        value (get @edit-state attribute)]
    [:div
     [labelled-input/view
      {:data-type :data.type/text-simple
       :label     (loc/tr :subject)
       :value     value
       :on-change (fn [new-value]
                    (if (nil? new-value)
                      (swap! edit-state dissoc attribute new-value)
                      (swap! edit-state assoc attribute new-value)))}]]))

(defn message-input
  [edit-state]
  (let [attribute :message
        value (get @edit-state attribute)]
    [:div
     [labelled-input/view
      {:data-type :data.type/text-multiline
       :label     (loc/tr :message)
       :value     value
       :on-change (fn [new-value]
                    (if (nil? new-value)
                      (swap! edit-state dissoc attribute new-value)
                      (swap! edit-state assoc attribute new-value)))}]]))

(defn footer-input
  [edit-state]
  (let [attribute :footer
        value (get @edit-state attribute)]
    [:div
     [labelled-input/view
      {:data-type :data.type/text-multiline
       :label     (loc/tr :email-footer)
       :value     value
       :on-change (fn [new-value]
                    (if (nil? new-value)
                      (swap! edit-state dissoc attribute new-value)
                      (swap! edit-state assoc attribute new-value)))}]]))

(defn save-default-checkbox
  [edit-state]
  (let [attribute :save-default
        value (get @edit-state attribute)]
    [:div
     [:div
      {:class "flex gap-3"}
      [checkbox/view
       {:value     value
        :on-change (fn [checked?]
                     (swap! edit-state assoc attribute checked?))}]
      [:span
       {:class "mb-0 text-sm"}
       (loc/tr :put-on-hold-curtain/make-this-default)]]]))

(defn view
  [{:keys [props state]}]
  (let [edit-state (r/cursor state [:edit])
        oppo-id (:oppo-id props)
        first-contact-person (->> oppo-id
                                  (fu/resolve-contact-persons)
                                  (remove (fn-> :person/universal-email-address string/blank?))
                                  first)
        deal (get @session/entities oppo-id)
        subject (loc/tr :put-on-hold-curtain/default-subject [(:oppo/name deal)])
        message (or (:tenant/put-on-hold-template @session/tenant)
                    (loc/tr :put-on-hold-curtain/default-message))
        footer (loc/tr :put-on-hold-curtain/default-footer [(:account/first-name @session/account)])
        default-months 1
        _ (swap! edit-state assoc
                 :subject subject
                 :message message
                 :footer footer
                 :months default-months)
        _ (when first-contact-person
            (swap! edit-state assoc :notify-to (:db/id first-contact-person)))
        nylas-enabled? (session/nylas-active?)
        state-atom (r/cursor state [:state-atom])]
    (fn [{:keys [shown? close-modal! props state topmost?]}]
      (let [{:keys [header-text
                    oppo-id reset-focus!]}
            props
            session-entities @session/entities
            deal (get session-entities oppo-id)
            deal-name (:oppo/name deal)
            close-modal! (fn []
                           (close-modal!)
                           (when reset-focus!
                             (reset-focus!)))
            header-text (or header-text (loc/tr :put-on-hold-curtain/title [deal-name]))

            edit-vals @edit-state
            {:keys [months until subject message notify-to]} edit-vals

            date-not-selected? (or (not (some #{months} (conj preset-months 0)))
                                   (and (zero? months)
                                        (nil? until)))

            contact-persons (->> oppo-id
                                 (fu/resolve-contact-persons)
                                 (remove :common/disabled?)
                                 (remove (fn-> :person/universal-email-address string/blank?)))
            no-contacts? (empty? contact-persons)
            notify? (:send-notification edit-vals)
            _ (when (and no-contacts?
                         notify?)
                (swap! edit-state assoc :send-notification false))

            one-contact? (= (count contact-persons) 1)
            first-contact-id (when one-contact?
                               (-> contact-persons
                                   first
                                   :db/id))
            _ (when (and one-contact?
                         (not= notify-to first-contact-id))
                (swap! edit-state assoc :notify-to first-contact-id))

            notification-tooltip (when no-contacts?
                                   (loc/tr :pass-curtain/add-contact))

            contact (->> notify-to
                         (get session-entities))
            not-contact-person? (->> contact-persons
                                     (filter (fn-> :db/id (= notify-to)))
                                     empty?)
            no-contact? (or not-contact-person?
                            (nil? contact)
                            (:common/disabled? contact))
            no-email? (->> contact
                           :person/universal-email-address
                           string/blank?)

            empty-subject? (string/blank? subject)
            empty-message? (string/blank? message)

            button-disabled? (if (and notify? (not nylas-enabled?))
                               (or date-not-selected?
                                   no-email?
                                   no-contact?
                                   empty-subject?
                                   empty-message?)
                               date-not-selected?)
            reject! (fn []
                      (events/on-put-on-hold state deal close-modal!))]
        [modal/view
         {:visible?      (and shown? topmost?)
          :hide!         close-modal!
          :backdrop      "static"
          :color         ::c/warning
          :close-button? true}
         [:<>
          [:i.fal.fa-pause-circle.mr-2]
          header-text]
         [:div
          {:class "flex flex-col gap-4"}
          [month-selector edit-state]
          (when (zero? months)
            [until-selector edit-state])

          [internal-comment-input edit-state]

          [:div
           {:class "flex flex-col gap-2"}
           [:div
            {:class "flex gap-2 items-base text-base-light-text-paler text-xs"}
            [:i.fal.fa-user-secret]
            [:div
             (loc/tr :pass-curtain/private-info)]]

           [notification-toggle edit-state notification-tooltip]]

          (when (and notify?
                     (not nylas-enabled?))
            [:div
             {:class "flex flex-col gap-4"}
             [to-selector edit-state contact-persons]
             [subject-input edit-state state-atom]
             [message-input edit-state state-atom]
             [footer-input edit-state state-atom]
             (when (session/is-admin?)
               [save-default-checkbox edit-state state-atom])])]
         [:<>
          [modal/left-cancel-only close-modal! nil]
          [modal/right-single-button
           nil
           button-disabled?
           #(reject!)
           (if notify?
             (loc/tr :put-on-hold-curtain/put-on-hold-and-notify)
             (loc/tr :put-on-hold-curtain/put-on-hold-without-notifying))
           nil]]]))))
